import { useState } from 'react'
import { Plus, Pencil, Trash2, Megaphone } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useAnnouncements, Announcement } from '@/hooks/useAnnouncements'
import { announcementService, CreateAnnouncementInput, UpdateAnnouncementInput } from '@/services/announcement_service'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'
import { useMe } from '@/hooks/useMe'

const TARGET_TYPE_LABELS: Record<string, string> = {
  clinic: '診所',
  ad_supplier: '廣告商',
  all: '全部',
}

const TARGET_TYPE_VARIANTS: Record<string, 'default' | 'secondary' | 'outline'> = {
  clinic: 'default',
  ad_supplier: 'secondary',
  all: 'outline',
}

export default function AnnouncementsPage() {
  const { token } = useAuth()
  const { user } = useMe()
  const { mode, id: viewModeId } = useViewMode()
  const [page, setPage] = useState(1)

  // sys_admin 在 provider mode 時用 viewMode 的 id，否則 API 會用 token 中的 provider_id
  const isSysAdmin = user?.role_type === 'sys_admin'
  const providerId = isSysAdmin && mode === 'provider' ? viewModeId : user?.provider_id

  const { announcements, total, isLoading, mutate } = useAnnouncements({
    page,
    pageSize: 20,
    sortBy: 'sort_order',
    sortOrder: 'asc',
    providerId,
  })

  const [formOpen, setFormOpen] = useState(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Announcement | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Form state
  const [formTitle, setFormTitle] = useState('')
  const [formContent, setFormContent] = useState('')
  const [formTargetType, setFormTargetType] = useState<'clinic' | 'ad_supplier' | 'all'>('all')
  const [formIsActive, setFormIsActive] = useState(true)
  const [formSortOrder, setFormSortOrder] = useState(0)

  const openCreateForm = () => {
    setEditingAnnouncement(null)
    setFormTitle('')
    setFormContent('')
    setFormTargetType('all')
    setFormIsActive(true)
    setFormSortOrder(0)
    setFormOpen(true)
  }

  const openEditForm = (announcement: Announcement) => {
    setEditingAnnouncement(announcement)
    setFormTitle(announcement.title)
    setFormContent(announcement.content)
    setFormTargetType(announcement.target_type)
    setFormIsActive(announcement.is_active)
    setFormSortOrder(announcement.sort_order)
    setFormOpen(true)
  }

  const handleSubmit = async () => {
    if (!token || !formTitle.trim() || !formContent.trim()) return
    setSubmitting(true)

    try {
      if (editingAnnouncement) {
        const data: UpdateAnnouncementInput = {
          title: formTitle,
          content: formContent,
          target_type: formTargetType,
          is_active: formIsActive,
          sort_order: formSortOrder,
        }
        await announcementService.update(editingAnnouncement.announcement_id, data, token)
      } else {
        const data: CreateAnnouncementInput = {
          title: formTitle,
          content: formContent,
          target_type: formTargetType,
          is_active: formIsActive,
          sort_order: formSortOrder,
          ...(isSysAdmin && providerId ? { provider_id: providerId } : {}),
        }
        await announcementService.create(data, token)
      }
      setFormOpen(false)
      mutate()
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleActive = async (announcement: Announcement) => {
    if (!token) return
    await announcementService.update(
      announcement.announcement_id,
      { is_active: !announcement.is_active },
      token,
    )
    mutate()
  }

  const handleDelete = async () => {
    if (!token || !deleteTarget) return
    await announcementService.delete(deleteTarget.announcement_id, token)
    setDeleteTarget(null)
    mutate()
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')} ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="工具箱"
        title="公告管理"
        description="建立與管理對旗下單位的公告訊息"
        icon={<Megaphone className="h-8 w-8" />}
        level="section"
      >
        <Button onClick={openCreateForm}>
          <Plus className="h-4 w-4 mr-2" />
          新增公告
        </Button>
      </PageHeader>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <p className="text-center text-muted-foreground py-8">載入中...</p>
          ) : announcements.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">尚無公告</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">排序</TableHead>
                  <TableHead>標題</TableHead>
                  <TableHead className="w-[100px]">對象</TableHead>
                  <TableHead className="w-[80px]">狀態</TableHead>
                  <TableHead className="w-[160px]">建立時間</TableHead>
                  <TableHead className="w-[100px]">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {announcements.map((item) => (
                  <TableRow key={item.announcement_id}>
                    <TableCell>{item.sort_order}</TableCell>
                    <TableCell className="font-medium">{item.title}</TableCell>
                    <TableCell>
                      <Badge variant={TARGET_TYPE_VARIANTS[item.target_type]}>
                        {TARGET_TYPE_LABELS[item.target_type]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={item.is_active}
                        onCheckedChange={() => handleToggleActive(item)}
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(item.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditForm(item)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteTarget(item)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {total > 20 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                上一頁
              </Button>
              <span className="flex items-center text-sm text-muted-foreground">
                {page} / {Math.ceil(total / 20)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= Math.ceil(total / 20)}
                onClick={() => setPage(page + 1)}
              >
                下一頁
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAnnouncement ? '編輯公告' : '新增公告'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>標題</Label>
              <Input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="請輸入公告標題"
                maxLength={200}
              />
            </div>
            <div>
              <Label>內容</Label>
              <Textarea
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                placeholder="請輸入公告內容"
                rows={5}
              />
            </div>
            <div>
              <Label>公告對象</Label>
              <Select
                value={formTargetType}
                onValueChange={(v) => setFormTargetType(v as 'clinic' | 'ad_supplier' | 'all')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="clinic">診所</SelectItem>
                  <SelectItem value="ad_supplier">廣告商</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label>排序</Label>
                <Input
                  type="number"
                  value={formSortOrder}
                  onChange={(e) => setFormSortOrder(parseInt(e.target.value) || 0)}
                  min={0}
                />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <Label>啟用</Label>
                <Switch checked={formIsActive} onCheckedChange={setFormIsActive} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              取消
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formTitle.trim() || !formContent.trim()}
            >
              {submitting ? '儲存中...' : '儲存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete AlertDialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確認刪除</AlertDialogTitle>
            <AlertDialogDescription>
              確定要刪除公告「{deleteTarget?.title}」嗎？此操作無法復原。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
